import { useEffect, useState } from "react";
import { Sparkles, ExternalLink, Bus, Building, Utensils, Lightbulb, Loader2, RefreshCw, BadgeCheck } from "lucide-react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/* <AiContentPack> — hydrates airport/airline Overview with AI-generated content
   (tagline · intro · terminals · ground transport · nearby hotels · dining · tips)
   on first load, cached forever server-side. Falls silent if the entity is
   already rich in editorial content upstream. */

export default function AiContentPack({ code, kind = "airport" }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const isMod = !!(user && (user.is_moderator || user.role === "admin" || user.role === "moderator"));

  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    setData(null); setMeta(null); setErr(false); setLoading(true);
    const path = kind === "airline" ? "airline-content" : "airport-content";
    axios.get(`${API}/api/v1/${path}/${code}`)
      .then((r) => { if (!cancelled) { setData(r.data?.content || null); setMeta(r.data || null); } })
      .catch(() => { if (!cancelled) setErr(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [code, kind]);

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const path = kind === "airline" ? "airline-content" : "airport-content";
      const r = await axios.post(`${API}/api/v1/${path}/${code}/refresh`, {}, { withCredentials: true });
      setData(r.data?.content || null); setMeta(r.data || null);
      toast.success("Content regenerated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regeneration failed");
    } finally { setRegenerating(false); }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const r = await axios.post(`${API}/api/v1/ai-verify/${kind}/${code}`, {}, { withCredentials: true });
      setMeta((m) => ({ ...(m || {}), verified_by: r.data?.verified_by, verified_at: r.data?.verified_at }));
      toast.success("Marked verified");
    } catch (e) { toast.error(e?.response?.data?.detail || "Verify failed"); }
    finally { setVerifying(false); }
  };

  if (err) return null;
  if (loading) return (
    <div className="my-6 flex items-center gap-2 text-sm text-zinc-500" data-testid="ai-content-loading">
      <Loader2 className="w-4 h-4 animate-spin" /> Generating {kind} overview…
    </div>
  );
  if (!data) return null;

  return (
    <div className="relative">
      {isMod && (
        <div className="absolute -top-2 right-0 flex items-center gap-2">
          <button onClick={regenerate} disabled={regenerating} data-testid="ai-content-regenerate"
            className="inline-flex items-center gap-1.5 bg-white border border-zinc-300 hover:border-blue-400 rounded-full px-3 py-1.5 text-[11px] font-800 uppercase tracking-wide text-zinc-700 hover:text-blue-700 shadow-sm disabled:opacity-60 transition-colors">
            {regenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {regenerating ? "Regenerating…" : "Regenerate"}
          </button>
          {!meta?.verified_by && (
            <button onClick={verify} disabled={verifying} data-testid="ai-content-verify"
              className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-3 py-1.5 text-[11px] font-800 uppercase tracking-wide shadow-sm disabled:opacity-60 transition-colors">
              {verifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BadgeCheck className="w-3.5 h-3.5" />}
              Verify
            </button>
          )}
        </div>
      )}
      {meta?.verified_by && (
        <div className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 text-[10px] font-800 uppercase tracking-wider text-emerald-700 mb-3" data-testid="ai-content-verified">
          <BadgeCheck className="w-3.5 h-3.5" /> Verified by editor
        </div>
      )}
      {kind === "airline" ? <AirlinePack data={data} /> : <AirportPack data={data} />}
    </div>
  );
}

function Section({ icon: Icon, title, children, testid }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2 text-[13px] font-800 uppercase tracking-wide text-zinc-800">
        <Icon className="w-4 h-4 text-blue-600" /> {title}
      </div>
      {children}
    </div>
  );
}

function AirportPack({ data }) {
  return (
    <section className="my-6 space-y-4" data-testid="ai-airport-pack">
      {data.hero_tagline && (
        <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-full px-3 py-1 text-[11px] font-800 uppercase tracking-wider text-blue-700" data-testid="ai-hero-tagline">
          <Sparkles className="w-3.5 h-3.5" /> {data.hero_tagline}
        </div>
      )}
      {data.intro && (
        <p className="text-[15px] leading-relaxed text-zinc-800" data-testid="ai-intro">{data.intro}</p>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        {Array.isArray(data.terminals) && data.terminals.length > 0 && (
          <Section icon={Building} title="Terminals" testid="ai-terminals">
            <ul className="space-y-1.5">
              {data.terminals.map((t, i) => (
                <li key={i} className="text-sm text-zinc-700">
                  <span className="font-800 text-zinc-900">{t.name}</span>{t.description ? ` — ${t.description}` : ""}
                </li>
              ))}
            </ul>
          </Section>
        )}
        {Array.isArray(data.ground_transport) && data.ground_transport.length > 0 && (
          <Section icon={Bus} title="Ground Transport" testid="ai-ground-transport">
            <ul className="space-y-1.5 text-sm text-zinc-700 list-disc pl-5">
              {data.ground_transport.map((g, i) => <li key={i}>{g}</li>)}
            </ul>
          </Section>
        )}
        {Array.isArray(data.nearby_hotels) && data.nearby_hotels.length > 0 && (
          <Section icon={Building} title="Nearby Hotels" testid="ai-nearby-hotels">
            <ul className="space-y-1.5 text-sm text-zinc-700 list-disc pl-5">
              {data.nearby_hotels.map((h, i) => <li key={i}>{h}</li>)}
            </ul>
          </Section>
        )}
        {data.dining_hints && (
          <Section icon={Utensils} title="Dining" testid="ai-dining">
            <p className="text-sm text-zinc-700">{data.dining_hints}</p>
          </Section>
        )}
      </div>
      {Array.isArray(data.local_tips) && data.local_tips.length > 0 && (
        <Section icon={Lightbulb} title="Traveller Tips" testid="ai-tips">
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-zinc-700 list-disc pl-5">
            {data.local_tips.map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        </Section>
      )}
      {data.official_website && (
        <a href={data.official_website} target="_blank" rel="noopener noreferrer" data-testid="ai-official-site"
          className="inline-flex items-center gap-1.5 text-sm font-800 text-blue-700 hover:text-blue-900">
          <ExternalLink className="w-4 h-4" /> Official website
        </a>
      )}
      <p className="text-[10px] text-zinc-400 mt-1" data-testid="ai-content-note">AI-assisted overview, verified against public sources.</p>
    </section>
  );
}

function AirlinePack({ data }) {
  return (
    <section className="my-6 space-y-4" data-testid="ai-airline-pack">
      {data.hero_tagline && (
        <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-full px-3 py-1 text-[11px] font-800 uppercase tracking-wider text-blue-700" data-testid="ai-hero-tagline">
          <Sparkles className="w-3.5 h-3.5" /> {data.hero_tagline}
        </div>
      )}
      {data.intro && (
        <p className="text-[15px] leading-relaxed text-zinc-800" data-testid="ai-intro">{data.intro}</p>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        {Array.isArray(data.primary_hubs) && data.primary_hubs.length > 0 && (
          <Section icon={Building} title="Primary Hubs" testid="ai-primary-hubs">
            <div className="flex flex-wrap gap-1.5">
              {data.primary_hubs.map((h) => (
                <a key={h} href={`/airport/${h}`} className="inline-flex items-center bg-zinc-100 hover:bg-blue-100 hover:text-blue-800 text-zinc-800 font-mono font-800 text-xs px-2.5 py-1 rounded transition-colors">{h}</a>
              ))}
            </div>
          </Section>
        )}
        {Array.isArray(data.cabin_classes) && data.cabin_classes.length > 0 && (
          <Section icon={Sparkles} title="Cabin Classes" testid="ai-cabins">
            <div className="flex flex-wrap gap-1.5">
              {data.cabin_classes.map((c) => (
                <span key={c} className="inline-block bg-zinc-100 text-zinc-800 text-xs font-700 px-2.5 py-1 rounded">{c}</span>
              ))}
            </div>
          </Section>
        )}
        {data.loyalty_program && (
          <Section icon={Sparkles} title="Loyalty Program" testid="ai-loyalty">
            <p className="text-sm text-zinc-700">{data.loyalty_program}</p>
          </Section>
        )}
        {data.fleet_note && (
          <Section icon={Building} title="Fleet Note" testid="ai-fleet-note">
            <p className="text-sm text-zinc-700">{data.fleet_note}</p>
          </Section>
        )}
      </div>
      <div className="flex flex-wrap gap-4">
        {data.baggage_policy_url && (
          <a href={data.baggage_policy_url} target="_blank" rel="noopener noreferrer" data-testid="ai-baggage-url"
            className="inline-flex items-center gap-1.5 text-sm font-800 text-blue-700 hover:text-blue-900">
            <ExternalLink className="w-4 h-4" /> Baggage policy
          </a>
        )}
        {data.contact_url && (
          <a href={data.contact_url} target="_blank" rel="noopener noreferrer" data-testid="ai-contact-url"
            className="inline-flex items-center gap-1.5 text-sm font-800 text-blue-700 hover:text-blue-900">
            <ExternalLink className="w-4 h-4" /> Contact & support
          </a>
        )}
      </div>
      <p className="text-[10px] text-zinc-400 mt-1" data-testid="ai-content-note">AI-assisted overview, verified against public sources.</p>
    </section>
  );
}
